import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 11  -  Mathematics of Relativity
// ============================================

/** 11.2 – Galilean vs Lorentz Transformations */
export const GalileanVsLorentz = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Galilean vs Lorentz Transformations', W / 2, 24);

        // Galilean card
        const lx = 20, ly = 45, lw = 245, lh = 200;
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(lx, ly, lw, lh, 12); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(lx, ly, lw, lh, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Galilean (v ≪ c)', lx + lw / 2, ly + 24);
        const galEqs = ["x' = x − vt", "t' = t", "v_total = v₁ + v₂", "Time is absolute"];
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif';
        galEqs.forEach((eq, i) => ctx.fillText(eq, lx + lw / 2, ly + 55 + i * 28));
        ctx.fillStyle = '#93C5FD'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Works for everyday speeds', lx + lw / 2, ly + lh - 12);

        // Lorentz card
        const rx = 295, ry = 45, rw = 245, rh = 200;
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(rx, ry, rw, rh, 12); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(rx, ry, rw, rh, 12); ctx.stroke();
        ctx.fillStyle = '#D97706'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('Lorentz (any v < c)', rx + rw / 2, ry + 24);
        const lorEqs = ["x' = γ(x − vt)", "t' = γ(t − vx/c²)", "v = (v₁+v₂)/(1+v₁v₂/c²)", "Time is relative"];
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif';
        lorEqs.forEach((eq, i) => ctx.fillText(eq, rx + rw / 2, ry + 55 + i * 28));
        ctx.fillStyle = '#FCD34D'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Required near speed of light', rx + rw / 2, ry + rh - 12);

        // Arrow
        ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 20px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('→', 275, ly + lh / 2);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('γ = 1/√(1 − v²/c²)   -   the Lorentz factor', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('At low speeds γ ≈ 1, and Lorentz reduces to Galilean', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 11.3 – Deriving the Lorentz Transformation (case study) */
export const LorentzDerivation = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Deriving the Lorentz Transformation', W / 2, 24);

        const steps = [
            { n: '1', title: 'Postulates', desc: 'Laws of physics same\nin all inertial frames;\nc = constant', color: '#3B82F6' },
            { n: '2', title: 'Linearity', desc: 'x\' = A·x + B·t\nt\' = C·x + D·t\n(linear mapping)', color: '#22C55E' },
            { n: '3', title: 'Light Cone', desc: 'x = ct → x\' = ct\'\nPreserves the\nlight cone', color: '#F59E0B' },
            { n: '4', title: 'Result', desc: 'x\' = γ(x − vt)\nt\' = γ(t − vx/c²)\nγ = 1/√(1−β²)', color: '#8B5CF6' },
        ];

        steps.forEach((s, i) => {
            const x = 15 + i * 137, y = 50, w = 125, h = 175;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            // Step number circle
            ctx.beginPath(); ctx.arc(x + w / 2, y + 22, 14, 0, Math.PI * 2);
            ctx.fillStyle = s.color; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.n, x + w / 2, y + 27);

            ctx.fillStyle = s.color; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(s.title, x + w / 2, y + 52);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            s.desc.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 72 + li * 15));

            if (i < steps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 18px Inter,sans-serif';
                ctx.fillText('→', x + w + 4, y + h / 2);
            }
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Key insight: requiring c to be the same in all frames uniquely determines the transformation', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Einstein (1905)  -  "On the Electrodynamics of Moving Bodies"', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 11.4 – Four-Vectors and Spacetime Algebra */
export const FourVectorDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Four-Vectors in Spacetime', W / 2, 24);

        const vectors = [
            { name: 'Position', symbol: 'x\u1D50', components: '(ct, x, y, z)', invariant: 's² = c²t² − x² − y² − z²', color: '#3B82F6' },
            { name: 'Momentum', symbol: 'p\u1D50', components: '(E/c, pₓ, pᵧ, p_z)', invariant: 'p² = E²/c² − |p|² = (mc)²', color: '#22C55E' },
            { name: 'Velocity', symbol: 'u\u1D50', components: 'γ(c, vₓ, vᵧ, v_z)', invariant: 'u·u = c²', color: '#F59E0B' },
        ];

        vectors.forEach((v, i) => {
            const x = 15 + i * 175, y = 50, w = 162, h = 155;
            ctx.fillStyle = v.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.fill();
            ctx.strokeStyle = v.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.stroke();

            ctx.fillStyle = v.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(v.name + ' 4-vector', x + w / 2, y + 24);

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
            ctx.fillText(v.symbol, x + w / 2, y + 50);

            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
            ctx.fillText(v.components, x + w / 2, y + 76);

            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText('Invariant:', x + w / 2, y + 104);
            ctx.fillStyle = v.color; ctx.font = 'bold 11px Inter,sans-serif';
            ctx.fillText(v.invariant, x + w / 2, y + 120);
        });

        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(40, 218, 460, 30, 8); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(40, 218, 460, 30, 8); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Four-vectors transform under Lorentz boosts  -  their "length" is invariant', W / 2, 238);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('The Minkowski metric η = diag(+1, −1, −1, −1) defines the inner product', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 11.5 – The Invariant Spacetime Interval */
export const SpacetimeInterval = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Invariant Spacetime Interval', W / 2, 24);

        // Central equation
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 20px Inter,sans-serif';
        ctx.fillText('Δs² = c²Δt² − Δx² − Δy² − Δz²', W / 2, 60);

        // Three interval types
        const types = [
            { name: 'Timelike', cond: 'Δs² > 0', desc: 'Events can be\ncausally connected.\nA slower-than-light\nsignal can link them.', color: '#3B82F6', icon: '⏱' },
            { name: 'Lightlike', cond: 'Δs² = 0', desc: 'Events connected\nby a light ray.\nOn the light cone\nboundary.', color: '#F59E0B', icon: '💡' },
            { name: 'Spacelike', cond: 'Δs² < 0', desc: 'No causal connection\npossible. Events are\n"elsewhere"  -  ordering\nis frame-dependent.', color: '#EF4444', icon: '📏' },
        ];

        types.forEach((t, i) => {
            const x = 15 + i * 175, y = 80, w = 162, h = 150;
            ctx.fillStyle = t.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = t.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.fillStyle = t.color; ctx.font = '20px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(t.icon, x + w / 2, y + 24);
            ctx.font = 'bold 13px Inter,sans-serif';
            ctx.fillText(t.name, x + w / 2, y + 44);
            ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(t.cond, x + w / 2, y + 62);

            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            t.desc.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 82 + li * 13));
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Δs² is the same in every inertial frame  -  it is a Lorentz invariant', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Proper time τ: for a timelike interval, c²Δτ² = Δs²', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 11.7 – Relativistic Velocity Addition */
export const RelativisticVelocityAddition = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Relativistic Velocity Addition', W / 2, 24);

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 18px Inter,sans-serif';
        ctx.fillText('v = (v₁ + v₂) / (1 + v₁v₂/c²)', W / 2, 56);

        // Comparison chart: v₁ = 0.8c, v₂ = 0.8c
        const m = { left: 80, right: 40, top: 80, bottom: 50 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        // Axes
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        // Y axis label
        ctx.save(); ctx.translate(22, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Result velocity (units of c)', 0, 0); ctx.restore();

        // X axis label
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('v₂ (units of c), with v₁ = 0.9c', m.left + pw / 2, H - 8);

        // Y gridlines
        [0, 0.5, 1.0, 1.5].forEach(v => {
            const y = m.top + ph - (v / 1.8) * ph;
            ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(m.left, y); ctx.lineTo(m.left + pw, y); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(v.toFixed(1) + 'c', m.left - 6, y + 4);
        });

        // c limit line
        const cY = m.top + ph - (1.0 / 1.8) * ph;
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(m.left, cY); ctx.lineTo(m.left + pw, cY); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('c (speed limit)', m.left + pw - 85, cY - 6);

        // Galilean line (v₁ + v₂)
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const v2 = (px / pw) * 1.0;
            const vGal = 0.9 + v2;
            const x = m.left + px;
            const y = m.top + ph - (vGal / 1.8) * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#94A3B8'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Galilean', m.left + pw - 48, m.top + 12);

        // Relativistic curve
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const v2 = (px / pw) * 1.0;
            const vRel = (0.9 + v2) / (1 + 0.9 * v2);
            const x = m.left + px;
            const y = m.top + ph - (vRel / 1.8) * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('Relativistic', m.left + pw - 65, cY + 18);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 11.10 – Minkowski Diagrams and Light Cones */
export const MinkowskiLightCones = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Minkowski Diagram  -  Light Cones', W / 2, 24);

        const ox = 270, oy = 180;

        // Future light cone fill
        ctx.fillStyle = '#DBEAFE40';
        ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(ox - 120, oy - 120); ctx.lineTo(ox + 120, oy - 120); ctx.closePath(); ctx.fill();
        // Past light cone fill
        ctx.fillStyle = '#FEE2E240';
        ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(ox - 120, oy + 120); ctx.lineTo(ox + 120, oy + 120); ctx.closePath(); ctx.fill();

        // Spacelike region label (left)
        ctx.fillStyle = '#94A3B8'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Spacelike', ox - 160, oy + 5);
        ctx.fillText('(elsewhere)', ox - 160, oy + 19);
        // Spacelike region label (right)
        ctx.fillText('Spacelike', ox + 160, oy + 5);
        ctx.fillText('(elsewhere)', ox + 160, oy + 19);

        // Light cone edges (45° lines)
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(ox - 140, oy + 140); ctx.lineTo(ox + 140, oy - 140); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(ox + 140, oy + 140); ctx.lineTo(ox - 140, oy - 140); ctx.stroke();

        // Axes
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(ox, oy - 150); ctx.lineTo(ox, oy + 150); ctx.stroke(); // ct axis
        ctx.beginPath(); ctx.moveTo(ox - 200, oy); ctx.lineTo(ox + 200, oy); ctx.stroke(); // x axis

        // Axis labels
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('ct', ox + 14, oy - 142);
        ctx.fillText('x', ox + 200, oy - 8);

        // Origin dot
        ctx.beginPath(); ctx.arc(ox, oy, 4, 0, Math.PI * 2); ctx.fillStyle = '#1E293B'; ctx.fill();

        // Worldline (timelike, curved)
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let t = 0; t <= 120; t++) {
            const x = ox + 30 * Math.sin(t / 30);
            const y = oy - t;
            t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#8B5CF6'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Worldline', ox + 38, oy - 90);
        ctx.fillText('(v < c)', ox + 38, oy - 76);

        // Labels
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Future', ox, oy - 105);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('Past', ox, oy + 116);
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Light cone (v = c)', ox + 75, oy - 93);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Massive particles always stay inside the light cone (timelike worldlines)', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 12  -  Relativistic Energy
// ============================================

/** 12.1 – Total Relativistic Energy */
export const TotalRelativisticEnergy = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Total Relativistic Energy', W / 2, 24);

        // Central formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 22px Inter,sans-serif';
        ctx.fillText('E = γmc²', W / 2, 62);

        // Breakdown
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif';
        ctx.fillText('E = Rest Energy + Kinetic Energy', W / 2, 88);

        // E vs v curve
        const m = { left: 80, right: 40, top: 105, bottom: 50 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        ctx.save(); ctx.translate(22, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('E / mc²', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('v / c', m.left + pw / 2, H - 8);

        // Curve: E = γmc² → E/mc² = γ
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const beta = (px / pw) * 0.99;
            const gamma = 1 / Math.sqrt(1 - beta * beta);
            const normE = Math.min(gamma, 8);
            const x = m.left + px;
            const y = m.top + ph - (normE / 8) * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // mc² baseline
        const mc2Y = m.top + ph - (1 / 8) * ph;
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(m.left, mc2Y); ctx.lineTo(m.left + pw, mc2Y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('mc² (rest energy)', m.left + 5, mc2Y - 6);

        // Y labels
        [1, 2, 4, 6, 8].forEach(v => {
            const y = m.top + ph - (v / 8) * ph;
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(v.toString(), m.left - 6, y + 4);
        });

        // X labels
        [0.2, 0.4, 0.6, 0.8].forEach(v => {
            const x = m.left + (v) * pw;
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(v.toFixed(1), x, m.top + ph + 14);
        });

        // Label curve
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('E = γmc²', m.left + pw * 0.65, m.top + 18);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('→ ∞ as v → c', m.left + pw * 0.65, m.top + 34);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 12.2 – Rest Energy: E₀ = mc² */
export const RestEnergyEquivalence = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Mass-Energy Equivalence: E₀ = mc²', W / 2, 24);

        // Big equation
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 28px Inter,sans-serif';
        ctx.fillText('E₀ = mc²', W / 2, 64);

        // Examples
        const examples = [
            { particle: 'Electron', mass: '0.511 MeV/c²', energy: '0.511 MeV', color: '#3B82F6' },
            { particle: 'Proton', mass: '938.3 MeV/c²', energy: '938.3 MeV', color: '#22C55E' },
            { particle: 'Neutron', mass: '939.6 MeV/c²', energy: '939.6 MeV', color: '#F59E0B' },
            { particle: '1 kg', mass: '1 kg', energy: '9 �- 10¹⁶ J', color: '#EF4444' },
        ];

        const th = 90;
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(30, th, W - 60, 26, [8, 8, 0, 0]); ctx.fill();
        ['Particle', 'Mass', 'Rest Energy'].forEach((h, i) => {
            ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, 30 + [70, 228, 420][i], th + 18);
        });

        examples.forEach((ex, ri) => {
            const y = th + 26 + ri * 32;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white'; ctx.fillRect(30, y, W - 60, 32);
            ctx.fillStyle = ex.color; ctx.fillRect(30, y, 4, 32);
            ctx.fillStyle = ex.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ex.particle, 100, y + 21);
            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
            ctx.fillText(ex.mass, 258, y + 21);
            ctx.fillText(ex.energy, 450, y + 21);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('1 kg of matter contains as much energy as 21.5 megatons of TNT', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 12.3 – Relativistic Kinetic Energy */
export const RelativisticKineticEnergy = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Relativistic vs Classical Kinetic Energy', W / 2, 24);

        // Formulas
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('Relativistic: K = (γ − 1)mc²', W / 2 - 100, 52);
        ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('Classical: K = ½mv²', W / 2 + 130, 52);

        // Plot
        const m = { left: 70, right: 40, top: 70, bottom: 50 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        ctx.save(); ctx.translate(18, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('K / mc²', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('v / c', m.left + pw / 2, H - 8);

        const maxK = 6;

        // Classical curve
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const beta = (px / pw) * 0.99;
            const kClass = 0.5 * beta * beta;
            const x = m.left + px;
            const y = m.top + ph - (kClass / maxK) * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Relativistic curve
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const beta = (px / pw) * 0.99;
            const gamma = 1 / Math.sqrt(1 - beta * beta);
            const kRel = Math.min(gamma - 1, maxK);
            const x = m.left + px;
            const y = m.top + ph - (kRel / maxK) * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Labels
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('(γ−1)mc²', m.left + pw * 0.6, m.top + 15);
        ctx.fillStyle = '#94A3B8';
        ctx.fillText('½mv²', m.left + pw * 0.82, m.top + ph - 25);

        // Divergence region
        ctx.fillStyle = '#FEF2F2'; ctx.globalAlpha = 0.3;
        const divX = m.left + 0.7 * pw;
        ctx.fillRect(divX, m.top, m.left + pw - divX, ph);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#EF4444'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Classical fails', divX + (m.left + pw - divX) / 2, m.top + ph - 8);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 12.4 – Energy-Momentum Relation (case study) */
export const EnergyMomentumRelation = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Energy-Momentum Relation', W / 2, 24);

        // Triangle diagram
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 20px Inter,sans-serif';
        ctx.fillText('E² = (pc)² + (mc²)²', W / 2, 58);

        const tx = 140, ty = 100;
        // Right triangle
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(tx, ty + 120); ctx.lineTo(tx + 200, ty + 120); ctx.lineTo(tx + 200, ty); ctx.closePath(); ctx.stroke();

        // Right angle marker
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(tx + 185, ty + 120); ctx.lineTo(tx + 185, ty + 105); ctx.lineTo(tx + 200, ty + 105); ctx.stroke();

        // Labels
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('E', tx + 85, ty + 55); // hypotenuse

        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('mc²', tx + 100, ty + 140); // base

        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('pc', tx + 218, ty + 65); // height

        // Special cases on right
        const rx = 380, ry = 85;
        ctx.fillStyle = '#F8FAFC'; ctx.beginPath(); ctx.roundRect(rx, ry, 155, 120, 10); ctx.fill();
        ctx.strokeStyle = '#E2E8F0'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(rx, ry, 155, 120, 10); ctx.stroke();

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Special Cases:', rx + 12, ry + 20);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('At rest (p=0):', rx + 12, ry + 42);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('E = mc²', rx + 105, ry + 42);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Massless (m=0):', rx + 12, ry + 68);
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('E = pc', rx + 120, ry + 68);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Ultra-rel (E≫mc²):', rx + 12, ry + 94);
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('E ≈ pc', rx + 135, ry + 94);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('This "Pythagorean" relation is the foundation of all particle physics kinematics', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 12.5 – Natural Units */
export const NaturalUnitsChart = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Natural Units  -  Setting c = 1 and ℏ = 1', W / 2, 24);

        const rows = [
            { quantity: 'Energy', si: 'Joule (J)', natural: 'eV, MeV, GeV', color: '#EF4444' },
            { quantity: 'Mass', si: 'kg', natural: 'eV/c² → eV', color: '#3B82F6' },
            { quantity: 'Momentum', si: 'kg·m/s', natural: 'eV/c → eV', color: '#22C55E' },
            { quantity: 'Time', si: 's', natural: '1/eV (ℏ/eV)', color: '#F59E0B' },
            { quantity: 'Length', si: 'm', natural: '1/eV (ℏc/eV)', color: '#8B5CF6' },
        ];

        const th = 54;
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(25, th, W - 50, 28, [8, 8, 0, 0]); ctx.fill();
        ['Quantity', 'SI Unit', 'Natural Unit'].forEach((h, i) => {
            ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, 25 + [80, 230, 400][i], th + 19);
        });

        rows.forEach((r, ri) => {
            const y = th + 28 + ri * 30;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white'; ctx.fillRect(25, y, W - 50, 30);
            ctx.fillStyle = r.color; ctx.fillRect(25, y, 4, 30);
            ctx.fillStyle = r.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(r.quantity, 105, y + 20);
            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
            ctx.fillText(r.si, 255, y + 20);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(r.natural, 425, y + 20);
        });

        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(50, 240, 440, 30, 8); ctx.fill();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('E² = p² + m²  (no factors of c needed!)', W / 2, 260);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 12.10 – Massless Particles */
export const MasslessUltrarelativistic = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Massless Particles & the Ultrarelativistic Limit', W / 2, 24);

        const cards = [
            { title: 'Photon (γ)', mass: '0', speed: 'c (always)', eqn: 'E = pc = hf', facts: 'Mediates EM force\nNo rest frame exists', color: '#F59E0B' },
            { title: 'Gluon (g)', mass: '0', speed: 'c (always)', eqn: 'E = pc', facts: 'Mediates strong force\nCarries color charge', color: '#EF4444' },
            { title: 'Ultrarelativistic', mass: 'm ≪ E/c²', speed: 'v ≈ c', eqn: 'E ≈ pc + m²c⁴/2E', facts: 'LHC protons at\n6.8 TeV: γ ≈ 7,200', color: '#3B82F6' },
        ];

        cards.forEach((cd, i) => {
            const x = 12 + i * 185, y = 48, w = 172, h = 160;
            ctx.fillStyle = cd.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.fill();
            ctx.strokeStyle = cd.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.stroke();

            ctx.fillStyle = cd.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(cd.title, x + w / 2, y + 22);

            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
            ctx.fillText('mass: ' + cd.mass, x + w / 2, y + 44);
            ctx.fillText('speed: ' + cd.speed, x + w / 2, y + 62);

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(cd.eqn, x + w / 2, y + 86);

            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
            cd.facts.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 108 + li * 14));
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Key: massless particles must travel at c  -  they have no rest frame', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Any particle with E ≫ mc² behaves approximately like a massless particle', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
